import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  Plus,
  Loader2,
  Bot,
  Brain,
  Trash2,
  Edit,
  Pin,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { TACO_MASCOT_URL, getCategoryBadgeColor, getVisibilityBadgeColor, formatCategory } from './adminUtils';

const TacoBrainTab = ({ api, tacoStats, tacoMemories, fetchData, fetchTacoMemories }) => {
  const [showNewMemoryDialog, setShowNewMemoryDialog] = useState(false);
  const [showEditMemoryDialog, setShowEditMemoryDialog] = useState(false);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [memorySearch, setMemorySearch] = useState('');
  const [memoryFilter, setMemoryFilter] = useState({ category: 'all', visibility: 'all' });
  const [creating, setCreating] = useState(false);
  const [newMemory, setNewMemory] = useState({
    content: '',
    scope: 'global',
    visibility: 'staff',
    category: 'cleaning_tips',
    tags: '',
    is_pinned: false,
  });

  const handleFetchMemories = async () => {
    try {
      const params = new URLSearchParams();
      if (memorySearch) params.append('search', memorySearch);
      if (memoryFilter.category !== 'all') params.append('category', memoryFilter.category);
      if (memoryFilter.visibility !== 'all') params.append('visibility', memoryFilter.visibility);
      
      const response = await api().get(`/admin/taco/memories?${params.toString()}`);
      fetchTacoMemories(response.data.memories || []);
    } catch (error) {
      toast.error('Failed to load memories');
    }
  };

  const handleCreateMemory = async () => {
    if (!newMemory.content.trim()) {
      toast.error('Please enter memory content');
      return;
    }
    setCreating(true);
    try {
      const params = new URLSearchParams({
        content: newMemory.content,
        scope: newMemory.scope,
        visibility: newMemory.visibility,
        category: newMemory.category,
        tags: newMemory.tags,
        is_pinned: newMemory.is_pinned,
      });
      
      await api().post(`/admin/taco/memories?${params.toString()}`);
      toast.success('Memory added to Taco\'s brain');
      setShowNewMemoryDialog(false);
      setNewMemory({ content: '', scope: 'global', visibility: 'staff', category: 'cleaning_tips', tags: '', is_pinned: false });
      handleFetchMemories();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create memory');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateMemory = async () => {
    if (!selectedMemory) return;
    setCreating(true);
    try {
      const params = new URLSearchParams();
      if (selectedMemory.content) params.append('content', selectedMemory.content);
      if (selectedMemory.visibility) params.append('visibility', selectedMemory.visibility);
      if (selectedMemory.category) params.append('category', selectedMemory.category);
      params.append('is_pinned', selectedMemory.is_pinned);
      
      await api().patch(`/admin/taco/memories/${selectedMemory.id}?${params.toString()}`);
      toast.success('Memory updated');
      setShowEditMemoryDialog(false);
      setSelectedMemory(null);
      handleFetchMemories();
    } catch (error) {
      toast.error('Failed to update memory');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    if (!confirm('Delete this memory?')) return;
    try {
      await api().delete(`/admin/taco/memories/${memoryId}`);
      toast.success('Memory deleted');
      handleFetchMemories();
      fetchData();
    } catch (error) {
      toast.error('Failed to delete memory');
    }
  };

  return (
    <div className="space-y-6" data-testid="taco-brain-content">
      {/* Header with Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={TACO_MASCOT_URL} alt="Taco" className="h-14 w-14 rounded-full border-2 border-amber-400" />
          <div>
            <h2 className="text-lg font-semibold text-zinc-900">Taco's Brain</h2>
            <p className="text-sm text-zinc-500">Manage Taco's shared knowledge base</p>
          </div>
        </div>
        <Button onClick={() => setShowNewMemoryDialog(true)} data-testid="add-memory-btn">
          <Plus className="mr-2 h-4 w-4" />
          Add Memory
        </Button>
      </div>

      {/* Stats Cards */}
      {tacoStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-zinc-100">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-zinc-900">{tacoStats.memories?.total || 0}</p>
              <p className="text-xs text-zinc-500">Total Memories</p>
            </CardContent>
          </Card>
          <Card className="border-zinc-100">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{tacoStats.memories?.pinned || 0}</p>
              <p className="text-xs text-zinc-500">Pinned</p>
            </CardContent>
          </Card>
          <Card className="border-zinc-100">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{tacoStats.conversations?.total || 0}</p>
              <p className="text-xs text-zinc-500">Conversations</p>
            </CardContent>
          </Card>
          <Card className="border-zinc-100">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{tacoStats.memories?.global || 0}</p>
              <p className="text-xs text-zinc-500">Global Memories</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card className="border-zinc-100">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Search memories..."
                value={memorySearch}
                onChange={(e) => setMemorySearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFetchMemories()}
                className="pl-10"
                data-testid="memory-search-input"
              />
            </div>
            <Select value={memoryFilter.category} onValueChange={(v) => setMemoryFilter({ ...memoryFilter, category: v })}>
              <SelectTrigger className="w-full md:w-40" data-testid="memory-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="cleaning_tips">Cleaning Tips</SelectItem>
                <SelectItem value="app_features">App Features</SelectItem>
                <SelectItem value="operations">Operations</SelectItem>
                <SelectItem value="safety">Safety</SelectItem>
                <SelectItem value="procedures">Procedures</SelectItem>
                <SelectItem value="faq">FAQ</SelectItem>
                <SelectItem value="preferences">Preferences</SelectItem>
              </SelectContent>
            </Select>
            <Select value={memoryFilter.visibility} onValueChange={(v) => setMemoryFilter({ ...memoryFilter, visibility: v })}>
              <SelectTrigger className="w-full md:w-40" data-testid="memory-visibility-filter"><SelectValue placeholder="Visibility" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Visibility</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="director">Director</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleFetchMemories} data-testid="memory-search-btn">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Memories List */}
      <Card className="border-zinc-100">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="h-5 w-5 text-amber-600" />
            Knowledge Base ({tacoMemories.length} memories)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 divide-y divide-zinc-100">
          {tacoMemories.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">
              <Brain className="h-12 w-12 mx-auto mb-4 text-zinc-300" />
              <p>No memories found</p>
              <p className="text-sm mt-1">Add knowledge to help Taco assist your team better</p>
            </div>
          ) : (
            tacoMemories.map((memory) => (
              <div key={memory.id} className="p-4 hover:bg-zinc-50 transition-colors" data-testid={`memory-item-${memory.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {memory.is_pinned && <Pin className="h-3 w-3 text-amber-500" />}
                      <Badge className={getCategoryBadgeColor(memory.category)}>{formatCategory(memory.category)}</Badge>
                      <Badge className={getVisibilityBadgeColor(memory.visibility)}>{memory.visibility}</Badge>
                      <Badge variant="outline" className="text-xs">{memory.scope}</Badge>
                    </div>
                    <p className="text-sm text-zinc-800 line-clamp-2">{memory.content}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400">
                      <span>Used {memory.usage_count || 0} times</span>
                      <span>Source: {memory.source}</span>
                      {memory.tags?.length > 0 && <span>Tags: {memory.tags.join(', ')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedMemory(memory); setShowEditMemoryDialog(true); }} data-testid={`edit-memory-${memory.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteMemory(memory.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-memory-${memory.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Memory Limits Info */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Bot className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">Memory Limits</p>
              <p className="text-amber-700 mt-1">Global: 1,000 memories | Per Role: 500 memories | Per User: 200 memories</p>
              <p className="text-amber-600 mt-1">Oldest non-pinned memories are automatically evicted when limits are reached.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* New Memory Dialog */}
      <Dialog open={showNewMemoryDialog} onOpenChange={setShowNewMemoryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src={TACO_MASCOT_URL} alt="Taco" className="h-8 w-8 rounded-full" />
              Add Memory to Taco's Brain
            </DialogTitle>
            <DialogDescription>Add knowledge that Taco can use to help your team. Memories are filtered by role permissions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="memory-content">Content *</Label>
              <Textarea id="memory-content" value={newMemory.content} onChange={(e) => setNewMemory({ ...newMemory, content: e.target.value })} placeholder="Enter knowledge for Taco (e.g., cleaning tips, procedures, app guidance)..." className="mt-2 min-h-[100px]" data-testid="new-memory-content" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newMemory.category} onValueChange={(v) => setNewMemory({ ...newMemory, category: v })}>
                  <SelectTrigger className="mt-2" data-testid="new-memory-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cleaning_tips">Cleaning Tips</SelectItem>
                    <SelectItem value="app_features">App Features</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="procedures">Procedures</SelectItem>
                    <SelectItem value="faq">FAQ</SelectItem>
                    <SelectItem value="preferences">Preferences</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visibility</Label>
                <Select value={newMemory.visibility} onValueChange={(v) => setNewMemory({ ...newMemory, visibility: v })}>
                  <SelectTrigger className="mt-2" data-testid="new-memory-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff (all roles)</SelectItem>
                    <SelectItem value="manager">Manager+ only</SelectItem>
                    <SelectItem value="director">Director only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scope</Label>
                <Select value={newMemory.scope} onValueChange={(v) => setNewMemory({ ...newMemory, scope: v })}>
                  <SelectTrigger className="mt-2" data-testid="new-memory-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (everyone)</SelectItem>
                    <SelectItem value="role">Role-specific</SelectItem>
                    <SelectItem value="user">User-specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="memory-tags">Tags (comma-separated)</Label>
                <Input id="memory-tags" value={newMemory.tags} onChange={(e) => setNewMemory({ ...newMemory, tags: e.target.value })} placeholder="stains, carpet, tips" className="mt-2" data-testid="new-memory-tags" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="memory-pinned" checked={newMemory.is_pinned} onCheckedChange={(checked) => setNewMemory({ ...newMemory, is_pinned: checked })} data-testid="new-memory-pinned" />
              <Label htmlFor="memory-pinned" className="flex items-center gap-1">
                <Pin className="h-3 w-3" />
                Pin this memory (prevents auto-eviction)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMemoryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateMemory} disabled={creating} data-testid="create-memory-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Memory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Memory Dialog */}
      <Dialog open={showEditMemoryDialog} onOpenChange={(open) => { setShowEditMemoryDialog(open); if (!open) setSelectedMemory(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5" /> Edit Memory</DialogTitle>
            <DialogDescription>Update the memory content and settings.</DialogDescription>
          </DialogHeader>
          {selectedMemory && (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-memory-content">Content</Label>
                <Textarea id="edit-memory-content" value={selectedMemory.content || ''} onChange={(e) => setSelectedMemory({ ...selectedMemory, content: e.target.value })} className="mt-2 min-h-[100px]" data-testid="edit-memory-content" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={selectedMemory.category} onValueChange={(v) => setSelectedMemory({ ...selectedMemory, category: v })}>
                    <SelectTrigger className="mt-2" data-testid="edit-memory-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cleaning_tips">Cleaning Tips</SelectItem>
                      <SelectItem value="app_features">App Features</SelectItem>
                      <SelectItem value="operations">Operations</SelectItem>
                      <SelectItem value="safety">Safety</SelectItem>
                      <SelectItem value="procedures">Procedures</SelectItem>
                      <SelectItem value="faq">FAQ</SelectItem>
                      <SelectItem value="preferences">Preferences</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visibility</Label>
                  <Select value={selectedMemory.visibility} onValueChange={(v) => setSelectedMemory({ ...selectedMemory, visibility: v })}>
                    <SelectTrigger className="mt-2" data-testid="edit-memory-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff (all roles)</SelectItem>
                      <SelectItem value="manager">Manager+ only</SelectItem>
                      <SelectItem value="director">Director only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="edit-memory-pinned" checked={selectedMemory.is_pinned || false} onCheckedChange={(checked) => setSelectedMemory({ ...selectedMemory, is_pinned: checked })} data-testid="edit-memory-pinned" />
                <Label htmlFor="edit-memory-pinned" className="flex items-center gap-1"><Pin className="h-3 w-3" /> Pin this memory</Label>
              </div>
              <div className="text-xs text-zinc-500 border-t pt-4 mt-4">
                <p>ID: {selectedMemory.id}</p>
                <p>Source: {selectedMemory.source}</p>
                <p>Used: {selectedMemory.usage_count || 0} times</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMemoryDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateMemory} disabled={creating} data-testid="update-memory-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Memory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TacoBrainTab;
